import { cn } from "@/lib/utils";
import { motion, useInView, type Transition } from "framer-motion";
import React, { useRef, useImperativeHandle, forwardRef, useEffect } from "react";

type HighlightDirection = "ltr" | "rtl" | "ttb" | "btt";
type TriggerType = "auto" | "inView" | "hover" | "ref";

interface UseInViewOptions {
  once?: boolean;
  initial?: boolean;
  amount?: number | "some" | "all";
}

interface TextHighlighterProps {
  children: React.ReactNode;
  highlightColor?: string;
  direction?: HighlightDirection;
  triggerType?: TriggerType;
  transition?: Transition;
  useInViewOptions?: UseInViewOptions;
  className?: string;
  // Legacy props for backwards compatibility
  isHighlighted?: boolean;
  onClick?: () => void;
}

export interface TextHighlighterRef {
  animate: (direction?: HighlightDirection) => void;
  reset: () => void;
}

/**
 * Animated text highlighter component with multiple trigger modes
 * Based on https://www.fancycomponents.dev/docs/components/text/text-highlighter
 */
export const TextHighlighter = forwardRef<TextHighlighterRef, TextHighlighterProps>(
  (
    {
      children,
      highlightColor = "hsl(48, 96%, 53%)", // Yellow color for annotations
      direction = "ltr",
      triggerType = "auto",
      transition = {
        type: "spring",
        stiffness: 200,
        damping: 25,
      },
      useInViewOptions = {
        once: true,
        initial: false,
        amount: 0.5,
      },
      className,
      // Legacy props
      isHighlighted = false,
      onClick,
    },
    ref
  ) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const isInView = useInView(spanRef, useInViewOptions);
    const [isHovered, setIsHovered] = React.useState(false);
    const [shouldAnimate, setShouldAnimate] = React.useState(false);

    // Determine if animation should play based on trigger type
    useEffect(() => {
      // Only animate if isHighlighted is true
      if (!isHighlighted) {
        setShouldAnimate(false);
        return;
      }

      if (triggerType === "auto") {
        setShouldAnimate(true);
      } else if (triggerType === "inView") {
        setShouldAnimate(isInView);
      } else if (triggerType === "hover") {
        setShouldAnimate(isHovered);
      }
    }, [triggerType, isInView, isHovered, isHighlighted]);

    // Expose animate and reset methods via ref
    useImperativeHandle(ref, () => ({
      animate: (_dir?: HighlightDirection) => {
        setShouldAnimate(true);
      },
      reset: () => {
        setShouldAnimate(false);
      },
    }));

    // Calculate background size and position based on direction
    const getBackgroundStyles = () => {
      const baseStyles = {
        backgroundImage: `linear-gradient(${highlightColor}, ${highlightColor})`,
        backgroundRepeat: "no-repeat",
        boxDecorationBreak: "clone",
        WebkitBoxDecorationBreak: "clone",
      } as const;

      switch (direction) {
        case "ltr":
          return {
            ...baseStyles,
            backgroundPosition: "0% 50%",
          };
        case "rtl":
          return {
            ...baseStyles,
            backgroundPosition: "100% 50%",
          };
        case "ttb":
          return {
            ...baseStyles,
            backgroundPosition: "50% 0%",
          };
        case "btt":
          return {
            ...baseStyles,
            backgroundPosition: "50% 100%",
          };
      }
    };

    const getBackgroundSize = () => {
      const isHorizontal = direction === "ltr" || direction === "rtl";
      return isHorizontal ? "100% 100%" : "100% 100%";
    };

    const getInitialBackgroundSize = () => {
      const isHorizontal = direction === "ltr" || direction === "rtl";
      return isHorizontal ? "0% 100%" : "100% 0%";
    };

    return (
      <motion.span
        ref={spanRef}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          "relative inline-block",
          onClick && "cursor-pointer",
          className
        )}
        style={getBackgroundStyles()}
        initial={{
          backgroundSize: getInitialBackgroundSize(),
        }}
        animate={{
          backgroundSize: shouldAnimate
            ? getBackgroundSize()
            : getInitialBackgroundSize(),
        }}
        transition={transition}
      >
        {children}
      </motion.span>
    );
  }
);

TextHighlighter.displayName = "TextHighlighter";
